import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { FileText, Paperclip } from "lucide-react";
import { UPLOAD_ACCEPT, type DriveFile, type UploadKind } from "@/api/drive";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/field";
import { capitalise } from "@/lib/format";
import { cn } from "@/lib/utils";

/** How a form files a document in Drive; given by the page, so forms need no QueryClient. */
export type DriveUploader = {
  /** False while the company Drive is not connected (or needs reconnecting). */
  ready: boolean;
  /** `label` (the payee, or what it was for) goes into the file's name in Drive. */
  upload: (file: File, kind: UploadKind, on: string, label: string) => Promise<DriveFile>;
};

type DriveLinkFieldProps = {
  id: string;
  /** The link field's label, e.g. "Bill link". */
  label: string;
  kind: UploadKind;
  /** The date (YYYY-MM-DD) whose financial year and month the file is filed under. */
  on: string;
  /** Goes into the file's name in Drive: the payee, or what it was for. */
  fileLabel?: string;
  value: string;
  onChange: (url: string) => void;
  error?: string;
  hint: string;
  drive?: DriveUploader;
  /** Told when an upload starts and ends, so the form can hold its save meanwhile. */
  onBusy?: (busy: boolean) => void;
};

const NOUNS: Record<UploadKind, string> = { bill: "bill", proof: "payment proof" };

/**
 * A bill or payment proof: attached (the server files it in Drive and its link fills itself
 * in), or, optionally, a link pasted to a file already in Drive. A failed upload changes
 * nothing. Without `drive` it is a plain link field.
 */
export function DriveLinkField({
  id,
  label,
  kind,
  on,
  fileLabel = "",
  value,
  onChange,
  error,
  hint,
  drive,
  onBusy,
}: DriveLinkFieldProps) {
  const picker = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [filedAs, setFiledAs] = useState("");
  const [failure, setFailure] = useState("");
  const [pasting, setPasting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const noun = NOUNS[kind];
  const ready = drive?.ready === true;

  async function attach(file: File | undefined): Promise<void> {
    if (!file || !drive?.ready || uploading !== null) return;
    setUploading(file.name);
    setFailure("");
    onBusy?.(true);
    try {
      const filed = await drive.upload(file, kind, on, fileLabel.trim());
      onChange(filed.url);
      setFiledAs(filed.name);
      setPasting(false);
    } catch (err) {
      setFailure(err instanceof Error ? err.message : "The upload failed. Please try again.");
    } finally {
      setUploading(null);
      onBusy?.(false);
    }
  }

  function choose(): void {
    if (ready && uploading === null) picker.current?.click();
  }

  function picked(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = ""; // so the same file can be picked again after a failure
    void attach(file);
  }

  function dragOver(event: DragEvent<HTMLDivElement>): void {
    if (!ready) return;
    event.preventDefault(); // allows the drop
    setDragging(true);
  }

  function dropped(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setDragging(false);
    void attach(event.dataTransfer.files[0]);
  }

  function setLink(url: string): void {
    setFiledAs("");
    onChange(url);
  }

  const linkField = (
    <TextField
      id={id}
      label={label}
      type="url"
      value={value}
      onChange={setLink}
      error={error}
      hint={hint}
    />
  );
  if (!drive) return linkField;

  const showLink = !ready || pasting || Boolean(error);
  const attached = ready && uploading === null && value !== "" && !showLink;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <p className="text-sm font-medium">{capitalise(noun)}</p>
      <input
        ref={picker}
        type="file"
        accept={UPLOAD_ACCEPT}
        aria-label={`${label} file`}
        className="hidden"
        onChange={picked}
      />
      {attached ? (
        <div className="flex min-w-0 items-center gap-2 rounded-md border bg-card p-2">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-sm" title={filedAs || value}>
            {filedAs || value}
          </span>
          <Button size="sm" variant="ghost" aria-label={`Replace ${noun}`} onClick={choose}>
            Replace
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Remove ${noun}`}
            onClick={() => setLink("")}
          >
            Remove
          </Button>
        </div>
      ) : (
        <div
          data-testid={`${id}-drop`}
          onClick={choose}
          onDragOver={dragOver}
          onDragLeave={() => setDragging(false)}
          onDrop={dropped}
          className={cn(
            "flex flex-col items-center gap-1 rounded-md border border-dashed p-4 text-center",
            ready ? "cursor-pointer hover:bg-accent/50" : "bg-muted/40",
            dragging && "border-primary bg-primary/5",
          )}
        >
          <Button size="sm" variant="outline" disabled={!ready || uploading !== null}>
            <Paperclip className="h-4 w-4" aria-hidden />
            {uploading === null ? `Attach ${noun}` : `Uploading ${uploading}…`}
          </Button>
          <span className="text-xs text-muted-foreground">
            {ready ? "PDF or photo, up to 10 MB. Or drop it here." : "PDF or photo, up to 10 MB."}
          </span>
        </div>
      )}
      {!ready && (
        <p className="text-xs text-muted-foreground">
          Attaching needs the company Google Drive connected:{" "}
          <Link to="/settings?tab=drive" className="text-primary hover:underline">
            Settings → Google Drive
          </Link>
        </p>
      )}
      {failure && (
        <p role="alert" className="text-xs text-destructive">
          {capitalise(failure)}
        </p>
      )}
      {showLink
        ? linkField
        : value === "" && (
            <button
              type="button"
              className="self-start text-xs text-primary hover:underline"
              onClick={() => setPasting(true)}
            >
              or paste a link
            </button>
          )}
    </div>
  );
}
